import { useState } from 'react';
import { Check, Eye } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ImproveTextButton } from './ImproveTextButton';

/**
 * "Seeking 2nd opinion" — al lado de "Working on it" en Claim Review.
 *
 * El caso real: el revisor no quiere aceptar ni negar todavía, quiere que alguien con más ojo
 * lo mire antes. Eso se venía resolviendo por WhatsApp, así que el claim se veía "en revisión"
 * cuando en realidad estaba esperando a una persona y nadie más lo sabía.
 *
 * Pide el MOTIVO igual que Cancel/Archive: sin él, al que le llega la tarea le cae un claim sin
 * pregunta y tiene que revisarlo entero para adivinar qué le están consultando.
 *
 * Ya pedido, el botón se vuelve un chip morado con el motivo en el tooltip y la única acción
 * que queda es cerrarlo — para que dos personas no pidan lo mismo dos veces.
 */
export function SecondOpinionButton(
  { claimId, pendingAt, reason, onChanged }:
  { claimId: string; pendingAt?: string | null; reason?: string | null; onChanged?: () => void },
) {
  const { t } = useI18n();
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Optimista: la cola no se recarga sola y el chip tiene que aparecer al instante.
  const [local, setLocal] = useState<{ at: string; reason: string } | null>(null);
  const [cerrado, setCerrado] = useState(false);

  const pendiente = cerrado ? null : local ?? (pendingAt ? { at: pendingAt, reason: reason ?? '' } : null);

  async function pedir() {
    if (!motivo.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<{ ok: true; assignedToName: string | null }>(`/api/claims/${claimId}/second-opinion`, { reason: motivo.trim() });
      setLocal({ at: new Date().toISOString(), reason: motivo.trim() });
      setCerrado(false);
      setAbierto(false); setMotivo('');
      setMsg(r.assignedToName ? t('so.sentTo', { name: r.assignedToName }) : t('so.sent'));
      onChanged?.();
    } catch (e) {
      // El error que de verdad pasa: nadie configurado todavía. Se dice qué hacer, no "Error".
      const code = e instanceof ApiError ? e.code : null;
      setMsg(code === 'second_opinion_user_not_set' ? t('so.notSet')
        : code === 'second_opinion_self' ? t('so.self')
        : e instanceof ApiError ? e.message : 'Error');
    } finally { setBusy(false); }
  }

  async function cerrar() {
    if (!window.confirm(t('common.confirmRemove'))) return;
    setBusy(true);
    try { await api.del(`/api/claims/${claimId}/second-opinion`); setCerrado(true); setLocal(null); onChanged?.(); }
    catch { /* ignore */ } finally { setBusy(false); }
  }

  if (pendiente) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span title={pendiente.reason || undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f3e8ff', border: '1px solid #a855f7', borderRadius: 8, padding: '2px 8px', fontSize: 'var(--fs-2xs)', color: '#6b21a8', fontWeight: 'var(--fw-bold)' }}>
          <Eye size={13} strokeWidth={1.75} /> {t('so.pending')}
        </span>
        <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} disabled={busy} onClick={cerrar}><Check size={13} strokeWidth={1.75} /> {t('so.resolve')}</button>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', color: '#6b21a8', borderColor: '#d8b4fe' }}
        disabled={busy} onClick={() => setAbierto((v) => !v)}><Eye size={13} strokeWidth={1.75} /> {t('so.ask')}</button>
      {msg && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{msg}</span>}
      {abierto && (
        // Caja de motivo igual a la de Cancel/Archive: se escribe acá mismo, sin abrir el claim.
        <span className="card" style={{ display: 'grid', gap: 6, padding: 8, minWidth: 260, borderColor: '#a855f7' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('so.reasonLabel')}</span>
          <textarea className="input" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={t('so.reasonPh')} />
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button className="btn sm" disabled={busy || !motivo.trim()} style={{ background: '#7e22ce', color: '#fff', borderColor: 'transparent' }} onClick={pedir}>
              {busy ? '…' : <><Eye size={13} strokeWidth={1.75} /> {t('so.send')}</>}
            </button>
            <button className="btn ghost sm" disabled={busy} onClick={() => { setAbierto(false); setMotivo(''); }}>{t('common.cancel')}</button>
            <ImproveTextButton value={motivo} onChange={setMotivo} />
          </span>
        </span>
      )}
    </span>
  );
}
